import React, { useEffect, useState } from 'react';
import Header from '../../components/Header';
import CustomButton from '../../components/CustomButton';
import CustomLine from '../../components/CustomLine';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Loader from '../../global/loader';
import apiRequest from '../../utils/apiRequest';
import toast from 'react-hot-toast';
import { log } from 'node:console';

type AllDataType = {
  id: number;
  isActive: string;
  name: string;
  image: string;
  rewardCode: string;
};

type DiscountResponse = {
  hasNext: boolean;
  data: DiscountCodeNode[];
  cursor: string;
};

type DiscountCodeNode = {
  id: string;
  typeId: string;
  codeDiscount: {
    title: string;
    summary: string;
    status: string;
    asyncUsageCount: number;
  };
};

type ProgramData = {
  id: string;
  title: string;
  summary: string;
  status: string;
  asyncUsageCount: number;
  typeId: string;
};

const programCardBaseClass = 'w-[24%] mx-1';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: '0px solid #000',
  boxShadow: 24,
  p: 4,
  outline: 'none',
  borderRadius: '20px',
};

const ProgramCard = (programData: ProgramData) => {
  const navigation = useNavigate();

  const handleClick = () => {
    navigation(
      `/addWaysToEarn?type=${programData.typeId}&id=${programData.id}`
    );
  };
  return (
    <div className='cursor-pointer' onClick={handleClick}>
      <div className='flex justify-between py-5'>
        <div
          className={clsx(programCardBaseClass, 'text-[15px] leading-[13px]')}
        >
          {programData.title}
        </div>
        <div className={clsx(programCardBaseClass)}>
          {programData.status === 'ACTIVE' ? (
            <span className='px-4 py-1 rounded-full  bg-green-200 text-[13px]'>
              Active
            </span>
          ) : (
            <span className='px-4 py-1 rounded-full  bg-[#DEDEDE] text-[13px]'>
              Draft
            </span>
          )}
        </div>

        <div className={clsx(programCardBaseClass, 'text-[13px] font-light')}>
          {programData?.summary}
        </div>
        <div className={clsx(programCardBaseClass, 'text-[13px] font-light')}>
          {programData?.asyncUsageCount} rewarded
        </div>
      </div>
      <CustomLine />
    </div>
  );
};

const CategoryCard = ({
  title,
  id,
  image,
}: {
  title: string;
  id: string;
  image: string;
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/addWaysToEarn?type=${id}`);
  };

  return (
    <div className='my-5 cursor-pointer' onClick={handleClick}>
      <div className='flex gap-3 px-3 mb-4'>
        <img className='w-[40px] h-[40px] rounded-lg' src={image} />
        <div className='text-[13px] font-semibold mt-2'>{title}</div>
      </div>
      <CustomLine />
    </div>
  );
};

const Program: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [rewardDataType, setRewardDataType] = useState<AllDataType[]>([]);
  const [rewardData, setRewardData] = useState<DiscountResponse>();
  const [cursors, setCursors] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<number>(0);
  const [loader, setLoader] = useState(true);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const getRewardTypeData = async () => {
    setLoader(true);
    const res = await apiRequest('rewardType');

    if (res?.error) {
      setRewardDataType([]);
    } else {
      setRewardDataType(res?.data);
    }
    setLoader(false);
  };

  const getRewardList = async (cursorId?: string) => {
    setLoader(true);

    const params = {
      ...(cursorId && { cursor: cursorId }),
    };

    const res = await apiRequest('getAllRewards', null, params);

    if (res?.error) {
      toast.error('Failed to get reward list');
      setRewardData(undefined);
    } else {
      setRewardData(res?.data);

      if (cursorId) {
        setCursors((prev) => [...prev, cursorId]);
      }
    }

    setLoader(false);
  };

  const handleNextPage = async () => {
    console.log('current page: ', currentCursor);

    if (rewardData?.hasNext) {
      setCurrentCursor((prev) => prev + 1);
      getRewardList(rewardData.cursor);
    }
  };

  const handlePreviousPage = async () => {
    console.log('current page: ', currentCursor);

    if (currentCursor > 1) {
      const previousCursor = cursors[currentCursor - 1] || undefined;
      setCurrentCursor((prev) => prev - 1);
      getRewardList(previousCursor);
    } else {
      setCurrentCursor((prev) => prev - 1);
      getRewardList();
    }
  };

  useEffect(() => {
    getRewardTypeData();
    getRewardList();
  }, []);

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <div className='flex justify-between items-center'>
            <span className='text-[20px] font-semibold'>Add way to earn</span>
            <FaTimes
              className='text-[30px] cursor-pointer'
              onClick={handleClose}
            />
          </div>

          <div className='mt-[50px]'>
            {rewardDataType &&
              rewardDataType.map((val) => {
                return (
                  <CategoryCard
                    key={val.id}
                    image={val?.image}
                    title={val?.name}
                    id={val?.rewardCode}
                  />
                );
              })}
          </div>
        </Box>
      </Modal>
      {loader && <Loader showHide={loader} />}
      {!loader && (
        <div className='px-5'>
          <CustomButton name='Add Ways to earn' onClick={handleOpen} />
          <Header title='Program' />

          <div className='flex justify-end py-2'>
            <button
              onClick={handlePreviousPage}
              disabled={currentCursor === 0}
              className='w-[77px] text-[12px] h-[30px] bg-white border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={!rewardData?.hasNext}
              className='w-[77px] text-[12px] bg-white h-[30px] border rounded-[3px] mr-[2px] hover:bg-[#f2f0f0] cursor-pointer disabled:bg-[#f2f0f0] disabled:text-[#9e9595]'
            >
              Next
            </button>
          </div>

          <div>
            <CustomLine />

            <div className='p-3'>
              {rewardData &&
                rewardData.data.map((val) => (
                  <ProgramCard
                    key={val.id}
                    id={val.id}
                    typeId={val.typeId}
                    title={val.codeDiscount.title}
                    summary={val.codeDiscount.summary}
                    status={val.codeDiscount.status}
                    asyncUsageCount={val.codeDiscount.asyncUsageCount}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Program;
